import React, { useState } from 'react';
import { Box, Typography, Button, Menu, MenuItem, Chip, Divider } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { useActiveChart } from '../../../contexts/ActiveChartContext';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  viewingLabel,
  chipLabel,
  chartColor,
  AGGREGATE_COLOR,
  AGGREGATE_LABEL,
  AGGREGATE_LABEL_SHORT,
} from '../utils/chartContext';

// Persistent, color-coded chart-context chip shown on every client-portal page.
// It answers "whose chart am I in" and doubles as the one-click Switch control.
// Three states, driven by the current surface's eligibility (single source of
// truth: ActiveChartContext):
//   - not shown  : <2 charts eligible for this surface (nothing to disambiguate)
//   - aggregate  : >=2 eligible, no chart drilled in -> neutral "Household" chip
//   - viewing    : a concrete chart active -> color-coded "Viewing: <name>" chip
export default function ActiveChartBanner() {
  const {
    linkedCharts,
    selfPatientId,
    viewerName,
    isViewingOwnChart,
    isAggregate,
    hasAggregateForSurface,
    surfaceEligibleIds,
    switchChart,
    clearActiveChart,
    loading,
    activeChartContext,
  } = useActiveChart();

  const [anchorEl, setAnchorEl] = useState(null);
  const isMobile = useIsMobile();

  // Only disambiguate when the CURRENT surface actually offers >=2 charts.
  // Covers the single-chart exception (no chip ever) and per-surface gating
  // (e.g. a partner chart that is ineligible for health is never offered here).
  if (loading || !hasAggregateForSurface) return null;

  const eligibleSet = new Set((surfaceEligibleIds || []).map((id) => String(id)));

  // Charts the user may drill into on THIS surface (self + eligible linked).
  const drillTargets = [
    ...(selfPatientId && eligibleSet.has(String(selfPatientId))
      ? [{ patient_id: selfPatientId, name: viewerName || 'My chart', relative_label: 'self', isSelf: true }]
      : []),
    ...linkedCharts
      .filter((c) => String(c.patient_id) !== String(selfPatientId))
      .filter((c) => eligibleSet.has(String(c.patient_id))),
  ];

  const color = isAggregate ? AGGREGATE_COLOR : chartColor(activeChartContext);

  const handleSwitchClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const handleSelect = (chart) => {
    switchChart(chart.patient_id, chart.case_id || null);
    handleMenuClose();
  };
  const handleBackToHousehold = () => {
    clearActiveChart();
    handleMenuClose();
  };

  const switchLabel = isAggregate ? 'Choose a chart' : 'Switch chart';

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-end' : 'space-between',
          gap: 1,
          px: 2,
          py: 0.75,
          bgcolor: color.bg,
          borderBottom: '3px solid',
          borderColor: color.main,
          flexWrap: 'wrap',
        }}
        data-testid="active-chart-chip"
        data-chip-state={isAggregate ? 'aggregate' : 'viewing'}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {isAggregate ? (
            <GroupIcon fontSize="small" sx={{ color: color.main }} />
          ) : isViewingOwnChart ? (
            <PersonIcon fontSize="small" sx={{ color: color.main }} />
          ) : (
            <GroupIcon fontSize="small" sx={{ color: color.main }} />
          )}
          {isMobile ? (
            <Chip
              size="small"
              label={isAggregate ? AGGREGATE_LABEL_SHORT : chipLabel(activeChartContext)}
              sx={{
                bgcolor: color.main,
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: color.text, fontSize: '0.85rem' }}
            >
              {isAggregate ? AGGREGATE_LABEL : viewingLabel(activeChartContext)}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={handleSwitchClick}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: color.text,
            borderColor: color.main,
          }}
        >
          {switchLabel}
        </Button>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {!isAggregate && (
          <MenuItem onClick={handleBackToHousehold}>{AGGREGATE_LABEL}</MenuItem>
        )}
        {!isAggregate && <Divider />}
        {drillTargets
          .filter((c) =>
            String(c.patient_id) !== String(activeChartContext?.patientId)
            || String(c.case_id || '') !== String(activeChartContext?.caseId || '')
          )
          .map((chart) => (
            <MenuItem
              key={`${chart.patient_id}::${chart.case_id || ''}`}
              onClick={() => handleSelect(chart)}
            >
              {chart.isSelf ? 'My chart' : chart.name || 'Linked Chart'}
              {!chart.isSelf && chart.relative_label ? ` (${chart.relative_label})` : ''}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}
